import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import Column from "sap/m/Column";
import Text from "sap/m/Text";
import Context from "sap/ui/model/Context";
import AuthService from "../service/AuthService";
import PluginService, { PluginKpi } from "../service/PluginService";
import PluginI18nService from "../service/PluginI18nService";
import EventBus from "sap/ui/core/EventBus";

interface TileNavigate {
	pluginId: string;
	view?: string;
	params?: Record<string, string>;
}

/**
 * Where a card is in its own lifecycle.
 *
 * A four-state machine rather than a boolean, because "finished" and "finished
 * with nothing" and "finished badly" are three different things to draw, and a
 * failed card has to stay failed - which a `finally` that clears one flag
 * cannot express.
 */
type CardState = "loading" | "ok" | "empty" | "error";

/**
 * A resolved colour intent.
 *
 * Same vocabulary as the manifest's `tone`, narrowed to what core will actually
 * paint after the zero-clamp in _toneOf.
 */
type CardTone =
	| "informative"
	| "negative"
	| "critical"
	| "highlight"
	| "positive"
	| "neutral";

/** One figure in a group's strip. */
interface StripCard {
	label: string;
	icon: string;
	value: string;
	footnote: string;
	tone: CardTone;
	state: CardState;
	/** The URL that owns this card, so Retry can re-run exactly that request. */
	retryUrl: string;
	navigate?: TileNavigate;
}

// --- grid cards --------------------------------------------------------
// The second container: N rows rather than one figure. A SIBLING of the strip,
// never an entry in it - the strip's FlexBox would otherwise build a blank
// GenericTile for every card.

interface DashboardTableColumn {
	label: string;
	align: "Begin" | "End";
	demandPopin: boolean;
	minScreenWidth: string;
}

interface DashboardTableCell {
	text: string;
}

interface DashboardTableRow {
	/**
	 * ⚠️ INVARIANT: cells.length === the table's columns.length, always.
	 *
	 * ColumnListItemRenderer drives its loop from COLUMNS and tolerates a short
	 * cells array by rendering an empty <td>, so a mismatch looks harmless. But
	 * every data cell is keyboard-focusable on desktop, and
	 * ColumnListItem.getContentAnnouncementOfCell does
	 * `aCells[oColumn.getInitialOrder()]` then `oCell.$()` with NO guard - so a
	 * missing cell is a TypeError the moment someone tabs into it. _toRow pads
	 * rather than skips for this reason.
	 */
	cells: DashboardTableCell[];
	navigate?: TileNavigate;
}

/** The pinned link at the bottom of a card. */
interface CardFooter {
	/** Already formatted, count substituted. Empty until the count arrives. */
	text: string;
	visible: boolean;
	navigate?: TileNavigate;
}

interface GridCard {
	title: string;
	icon: string;
	/** The line beside the title. Falls back to the plugin's name. */
	meta: string;
	tone: CardTone;
	columns: DashboardTableColumn[];
	rows: DashboardTableRow[];
	state: CardState;
	/** Shown in place of rows when there are none, or when the fetch failed. */
	noDataText: string;
	retryUrl: string;
	footer: CardFooter;
}

/**
 * One plugin's section: its heading, then its figures, then its cards.
 *
 * Grouped by the plugin that DECLARED the tiles (not by a tile's navigate
 * target), so an Invoices figure that opens another plugin still sits under
 * Invoices. Groups appear in the order of their first tile after the sort, so
 * the manifests' `order` still decides which business area comes first.
 */
interface DashboardGroup {
	title: string;
	icon: string;
	strip: StripCard[];
	cards: GridCard[];
}

/** One strip card awaiting a shared response: where to write, which key to read. */
interface KpiTarget {
	kind: "kpi";
	path: string;
	valueKey: string;
	/** The manifest's declared intent; the zero-clamp is applied at write time. */
	tone: CardTone;
	toneWhenZero: CardTone;
}

/** How to turn one row's field into one cell. Labels already resolved. */
interface CellSpec {
	key: string;
	format: "text" | "number" | "date";
	/** Coded value -> already-translated label. Empty for most columns. */
	map: Record<string, string>;
}

/**
 * One table awaiting a response.
 *
 * Everything here is resolved at build time, so the fetch handler needs neither
 * a resource bundle nor the plugin's identity - and `cells` is the same array
 * the columns were built from, which is what makes the cells/columns length
 * invariant structural rather than something to remember.
 */
interface TableTarget {
	kind: "table";
	path: string;
	rowsKey: string;
	limit: number;
	cells: CellSpec[];
	navigate?: TileNavigate;
	emptyText: string;
	failedText: string;
	/**
	 * The footer count is read off the SAME response as the rows - every list
	 * route in this portal returns `totalCount` beside `value` - so "Show all
	 * (23)" costs no second request.
	 */
	footerCountKey: string;
	footerLabel: string;
}

type FetchTarget = KpiTarget | TableTarget;

/** Rows to show when a table tile does not say. A dashboard, not a list page. */
const DEFAULT_ROW_LIMIT = 5;

/** Sort key for a tile whose manifest does not state one. */
const DEFAULT_ORDER = 1000;

const TONES: readonly CardTone[] = [
	"informative",
	"negative",
	"critical",
	"highlight",
	"positive",
	"neutral",
];

/** A manifest string, or "neutral" if it is not a tone this core knows. */
function toTone(value: unknown, fallback: CardTone = "neutral"): CardTone {
	return TONES.includes(value as CardTone) ? (value as CardTone) : fallback;
}

/**
 * @namespace com.ang.portal.controller
 */
export default class Dashboard extends BaseController {
	public onInit(): void {
		const oModel = new JSONModel({
			groups: [] as DashboardGroup[],
			busy: false,
		});
		this.setModel(oModel, "dashboard");
	}

	public refreshPage(): void {
		void this._loadAllData();
	}

	// The busy overlay covers only the phase where there is nothing to click:
	// fetching the plugin list and resolving KPI labels. It is cleared as soon
	// as the tiles are painted, so a tile whose own request has resolved can be
	// pressed while its neighbours are still loading. Each tile then reports
	// progress through its own `loading` flag (GenericTile "Loading" state).
	private async _loadAllData(): Promise<void> {
		const oModel = this.getModel("dashboard") as JSONModel;
		oModel.setProperty("/busy", true);

		try {
			await this._loadPluginKpis();
		} finally {
			oModel.setProperty("/busy", false);
		}
	}

	// Bumped on every load. The requests outlive the method that starts them, so
	// a stale response from a previous load must not write into the model - by
	// then the same /groups/G/strip/N path can belong to a different card. Only writes
	// stamped with the current generation apply.
	private kpiGeneration = 0;

	// Which cards read from which endpoint, rebuilt on every load. An instance
	// field rather than a local because Retry has to find a URL's targets again
	// after the load that created them has returned.
	private targetsByUrl = new Map<string, FetchTarget[]>();

	private async _loadPluginKpis(): Promise<void> {
		const oModel = this.getModel("dashboard") as JSONModel;
		// Bumping here is what invalidates every in-flight response from the
		// previous load. The per-write guard now lives in _fetchTargets, which
		// closes over the generation it was handed.
		const generation = ++this.kpiGeneration;

		let plugins;
		try {
			plugins = await PluginService.getPlugins();
		} catch {
			return;
		}

		// Shell-owned chrome for the table tiles. A plugin may override the empty
		// text with its own wording; the failure text is the shell's alone,
		// because a plugin cannot describe a failure to reach itself.
		const bundle = await this.getResourceBundle();
		const emptyFallback = bundle.getText("dashboardTableEmpty");
		const failedText = bundle.getText("dashboardTableFailed");

		// Resolution stays PER PLUGIN - getResolver is per-bundle - so the build
		// maps plugin -> entries first, and only then flattens and sorts.
		const built = await Promise.all(
			plugins
				.filter((p) => p.enabled && (p.kpis ?? []).length > 0)
				.map(async (p, pluginIndex) => {
					const kpis = p.kpis ?? [];

					// Labels may be "{i18n>key}" references into the plugin's own
					// bundle; literals pass through unchanged.
					//
					// ⚠️ EVERY translatable string has to be in this array. The
					// resolver fetches the bundle only if at least one value passed
					// here is a reference, so a plugin whose only references are
					// column headers would otherwise render them literally.
					const t = await PluginI18nService.getResolver(p.id, [
						p.displayName,
						...kpis.map((k) => k.label),
						...kpis.map((k) => k.footnote),
						...kpis.map((k) => k.meta),
						...kpis.map((k) => k.emptyText),
						...kpis.map((k) => k.footer?.label),
						...kpis.flatMap((k) => (k.columns ?? []).map((c) => c.label)),
						...kpis.flatMap((k) =>
							(k.columns ?? []).flatMap((c) =>
								Object.values(c.map ?? ({} as Record<string, string>)),
							),
						),
					]);

					const pluginName = t(p.displayName, p.name);
					// ⚠️ THE PLUGIN'S ICON WINS OVER THE KPI'S OWN `icon`, deliberately.
					// A manifest may still declare one per KPI (Tickets declares
					// in-progress/inbox/discussion), and the dashboard ignores it: the
					// icon's job here is to say WHERE a figure came from, and three
					// different marks for one plugin's three tiles says the opposite.
					// The group heading carries the same icon, so a figure, a card and
					// the heading above them read as one family.
					const pluginIcon = p.icon ?? "sap-icon://puzzle";
					const navOf = (k: PluginKpi): TileNavigate | undefined =>
						k.navigate
							? { pluginId: k.navigate.pluginId ?? p.id, ...k.navigate }
							: undefined;

					// One entry per manifest tile, carrying its own sort key. The
					// slot defaults from the type, so a manifest written before
					// slots existed keeps its figures in the strip and adds nothing
					// to the grid.
					return kpis.map((k, kpiIndex) => ({
						sort: [k.order ?? DEFAULT_ORDER, pluginIndex, kpiIndex] as const,
						slot: k.slot ?? (k.type === "table" ? "grid" : "strip"),
						url: k.url,
						kpi: k,
						t,
						pluginId: p.id,
						pluginName,
						pluginIcon,
						navigate: navOf(k),
					}));
				}),
		);

		// ⚠️ SORT BEFORE ASSIGNING PATHS. Model paths are /groups/G/strip/N and
		// /groups/G/cards/N taken from the final array indexes, so sorting
		// afterwards would slide every fetched value onto a different card -
		// silently, and with plausible-looking numbers. Same failure class as the
		// split-before-map rule this replaced.
		const entries = built.flat().sort((a, b) => {
			for (let i = 0; i < a.sort.length; i += 1) {
				if (a.sort[i] !== b.sort[i]) return a.sort[i] - b.sort[i];
			}
			return 0;
		});

		// One section per plugin, in the order its first tile sorts. Built while
		// walking the sorted entries, so a group's index is final the moment it
		// is created and every path below is stable.
		const groups: DashboardGroup[] = [];
		const groupIndex = new Map<string, number>();
		const groupOf = (entry: (typeof entries)[number]): number => {
			let index = groupIndex.get(entry.pluginId);
			if (index === undefined) {
				index = groups.length;
				groupIndex.set(entry.pluginId, index);
				groups.push({
					title: entry.pluginName,
					icon: entry.pluginIcon,
					strip: [],
					cards: [],
				});
			}
			return index;
		};
		const targets: Array<{ url: string; target: FetchTarget }> = [];

		entries.forEach((entry) => {
			const { kpi: k, t } = entry;
			const g = groupOf(entry);
			const group = groups[g];

			if (entry.slot === "grid") {
				const path = `/groups/${g}/cards/${group.cards.length}`;
				group.cards.push({
					title: t(k.label),
					icon: entry.pluginIcon,
					// The group heading already names the plugin, so only a
					// manifest's own meta line is shown beside the title.
					meta: k.meta ? t(k.meta) : "",
					tone: toTone(k.tone),
					columns: (k.columns ?? []).map((c) => ({
						label: t(c.label),
						align: c.align === "End" ? ("End" as const) : ("Begin" as const),
						// No popin inside a card. A card is already narrow, so
						// collapsing columns into a popin area buys nothing and
						// costs the row its shape.
						demandPopin: false,
						minScreenWidth: "",
					})),
					rows: [],
					state: "loading",
					noDataText: "",
					retryUrl: k.url,
					footer: {
						text: "",
						visible: false,
						navigate: k.footer?.navigate
							? {
									pluginId: k.footer.navigate.pluginId ?? entry.navigate?.pluginId ?? "",
									...k.footer.navigate,
								}
							: entry.navigate,
					},
				});
				targets.push({
					url: k.url,
					target: {
						kind: "table",
						path,
						rowsKey: k.rowsKey ?? "",
						limit: k.rowLimit && k.rowLimit > 0 ? k.rowLimit : DEFAULT_ROW_LIMIT,
						cells: (k.columns ?? []).map((c) => ({
							key: c.key,
							format: c.format ?? "text",
							map: Object.entries(c.map ?? ({} as Record<string, string>)).reduce(
								(out: Record<string, string>, [code, label]) => {
									out[code] = t(label);
									return out;
								},
								{},
							),
						})),
						navigate: entry.navigate,
						emptyText: k.emptyText ? t(k.emptyText) : emptyFallback,
						failedText: failedText,
						footerCountKey: k.footer?.countKey ?? "",
						footerLabel: k.footer ? t(k.footer.label) : "",
					},
				});
				return;
			}

			const path = `/groups/${g}/strip/${group.strip.length}`;
			group.strip.push({
				label: t(k.label),
				icon: entry.pluginIcon,
				value: "–",
				footnote: k.footnote ? t(k.footnote) : "",
				tone: "neutral",
				state: "loading",
				retryUrl: k.url,
				navigate: entry.navigate,
			});
			targets.push({
				url: k.url,
				target: {
					kind: "kpi",
					path,
					valueKey: k.valueKey ?? "",
					tone: toTone(k.tone),
					toneWhenZero: toTone(k.toneWhenZero),
				},
			});
		});

		if (generation !== this.kpiGeneration) return;
		oModel.setProperty("/groups", groups);

		if (targets.length === 0) return;

		// A plugin's KPIs almost always share one endpoint and differ only in
		// valueKey - every plugin here points all of its tiles at a single
		// /stats URL, and Delivery declares four of them. Fetching per tile
		// would call that endpoint four times for one identical payload, and
		// each call costs a full plugin-context rebuild (user vars, group vars,
		// a scan of every enabled plugin's metadata) plus its SAP count
		// queries. So group the tiles by URL and request each endpoint once.
		// Grid cards join the same map, keyed the same way: a card and a figure
		// pointing at one endpoint still cost a single request between them.
		//
		// Kept on the INSTANCE, not in a local, because Retry has to find a
		// URL's targets again long after this method returned.
		this.targetsByUrl = new Map<string, FetchTarget[]>();
		targets.forEach(({ url, target }) => {
			const existing = this.targetsByUrl.get(url);
			if (existing) {
				existing.push(target);
			} else {
				this.targetsByUrl.set(url, [target]);
			}
		});

		// Each endpoint writes its tiles the moment its request resolves, rather
		// than collecting every result and repainting once. A KPI backed by a
		// cached count appears immediately instead of waiting behind whichever
		// plugin is slowest - and one slow or hanging endpoint only keeps its
		// own tiles spinning.
		//
		// These requests are deliberately not awaited: returning here drops the
		// busy overlay, which is what makes an already-loaded tile clickable
		// while the rest are still in flight.
		this.targetsByUrl.forEach((targets, url) => {
			this._fetchTargets(url, targets, generation);
		});
	}

	/**
	 * Fetch one endpoint and write every card that reads from it.
	 *
	 * Extracted from the load so Retry can re-run exactly one URL. The write
	 * guard closes over the generation it was CALLED with, so a retry issued
	 * after a refresh (which bumps the generation) writes nothing - the cards it
	 * would have written no longer exist at those paths.
	 *
	 * There is no `finally`. The terminal state is set in then/catch, because a
	 * blanket "stop loading" at the end is exactly what would wipe an error
	 * state a moment after it was set.
	 */
	private _fetchTargets(
		url: string,
		targets: FetchTarget[],
		generation: number,
	): void {
		const oModel = this.getModel("dashboard") as JSONModel;
		const write = (path: string, value: unknown): void => {
			if (generation === this.kpiGeneration) {
				oModel.setProperty(path, value);
			}
		};

		void AuthService.authenticatedFetch(url)
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.json() as Promise<Record<string, unknown>>;
			})
			.then((data) => {
				targets.forEach((target) => {
					if (target.kind === "kpi") {
						const raw: unknown = target.valueKey ? data[target.valueKey] : data;
						// Only scalars can be rendered on a tile. Without a valueKey
						// the payload is the whole response object, which would
						// otherwise stringify to "[object Object]".
						const scalar =
							typeof raw === "string"
								? raw
								: typeof raw === "number" || typeof raw === "boolean"
									? String(raw)
									: "–";
						write(`${target.path}/value`, scalar);
						write(`${target.path}/tone`, this._toneOf(scalar, target));
						write(`${target.path}/state`, "ok");
						return;
					}

					// The row array's position differs per plugin - {value: []}
					// for the SAP-backed ones, {items: []} for Delivery, and an
					// empty rowsKey when the payload IS the array.
					const envelope: unknown = target.rowsKey ? data[target.rowsKey] : data;
					const rows = Array.isArray(envelope)
						? envelope.slice(0, target.limit)
						: [];
					write(
						`${target.path}/rows`,
						rows.map((row) => this._toRow(row as Record<string, unknown>, target)),
					);
					// A response that parsed but held nothing is not a failure, and
					// must not read like one.
					write(`${target.path}/noDataText`, rows.length ? "" : target.emptyText);
					write(`${target.path}/state`, rows.length ? "ok" : "empty");

					if (target.footerLabel) {
						// The count rides in on the same payload as the rows.
						const total = target.footerCountKey
							? data[target.footerCountKey]
							: undefined;
						write(
							`${target.path}/footer/text`,
							target.footerLabel.replace(
								"{0}",
								typeof total === "number" || typeof total === "string"
									? String(total)
									: String(rows.length),
							),
						);
						write(`${target.path}/footer/visible`, true);
					}
				});
			})
			.catch(() => {
				// A failing endpoint marks only its own cards; the rest of the
				// dashboard is unaffected.
				targets.forEach((target) => {
					if (target.kind === "kpi") {
						write(`${target.path}/value`, "–");
						// Never an alarm colour on a value we do not have.
						write(`${target.path}/tone`, "neutral");
					} else {
						write(`${target.path}/rows`, []);
						write(`${target.path}/noDataText`, target.failedText);
						write(`${target.path}/footer/visible`, false);
					}
					write(`${target.path}/state`, "error");
				});
			});
	}

	/**
	 * The colour a figure should be painted, given what its plugin declared.
	 *
	 * ⚠️ The second clause is a core invariant and is deliberately not
	 * configurable: a zero is never painted negative or critical, whatever the
	 * manifest asks for. An absence of things is not an alarm - which is the
	 * "Overdue Orders 0 with a warning icon" bug this dashboard shipped with.
	 *
	 * It carries no business knowledge, which is why it belongs here rather than
	 * in each manifest. A plugin that wants "0 overdue is good" says
	 * `toneWhenZero: "positive"`, and that is the plugin saying so.
	 */
	private _toneOf(value: string, target: KpiTarget): CardTone {
		const isZero = value !== "" && Number(value) === 0;
		if (!isZero) return target.tone;
		const zeroTone = target.toneWhenZero;
		return zeroTone === "negative" || zeroTone === "critical"
			? "neutral"
			: zeroTone;
	}

	/**
	 * Re-run the one request behind this card.
	 *
	 * Uses the CURRENT generation, not a captured one, so the write guard admits
	 * it. If the URL is no longer in the map a refresh has replaced it, and the
	 * binding context this was pressed from is stale - do nothing rather than
	 * fetch something whose result has nowhere to go.
	 */
	public onRetryPress(oEvent: Event): void {
		const ctx = this._contextOf(oEvent);
		if (!ctx) return;
		const url = ctx.getProperty("retryUrl") as string | undefined;
		if (!url) return;
		const targets = this.targetsByUrl.get(url);
		if (!targets) return;

		const oModel = this.getModel("dashboard") as JSONModel;
		targets.forEach((target) => {
			oModel.setProperty(`${target.path}/state`, "loading");
		});
		this._fetchTargets(url, targets, this.kpiGeneration);
	}

	/**
	 * One response row -> one model row.
	 *
	 * Cells are built by mapping over the target's cell specs, never over the
	 * row's own keys, so the count is structurally equal to the column count and
	 * a field missing from the response becomes an empty cell rather than a
	 * short array. See the invariant on DashboardTableRow for why that matters.
	 */
	private _toRow(
		row: Record<string, unknown>,
		target: TableTarget,
	): DashboardTableRow {
		return {
			cells: target.cells.map((spec) => ({
				text: this._formatCell(row[spec.key], spec),
			})),
			navigate: this._rowNavigate(row, target),
		};
	}

	private _formatCell(value: unknown, spec: CellSpec): string {
		// Only scalars can be a cell. A nested object or array in a response row
		// would otherwise render as "[object Object]"; an empty cell is the same
		// answer the missing-field case gives, and both are honest.
		if (
			typeof value !== "string" &&
			typeof value !== "number" &&
			typeof value !== "boolean"
		) {
			return "";
		}

		const text = String(value);
		// A code the plugin gave a label for. An unmapped value passes through,
		// so a partial map degrades to showing the raw code rather than a blank.
		if (spec.map[text] !== undefined) return spec.map[text];

		if (spec.format === "number") {
			const n = Number(value);
			return Number.isFinite(n) ? n.toLocaleString() : text;
		}
		if (spec.format === "date") {
			const d = new Date(text);
			// An unparseable date shows as it arrived. Better a raw string than
			// "Invalid Date" in a dashboard cell.
			return Number.isNaN(d.getTime()) ? text : d.toLocaleDateString();
		}
		return text;
	}

	/**
	 * Per-row navigation, from one declaration.
	 *
	 * A table's `navigate.params` may hold "${Field}" placeholders, substituted
	 * from this row - which is what lets one manifest entry open five different
	 * records. A placeholder naming a field the row does not carry drops the
	 * whole link, so a row is inert rather than opening the wrong record.
	 */
	private _rowNavigate(
		row: Record<string, unknown>,
		target: TableTarget,
	): TileNavigate | undefined {
		const nav = target.navigate;
		if (!nav) return undefined;
		if (!nav.params) return nav;

		const params: Record<string, string> = {};
		for (const [name, raw] of Object.entries(nav.params)) {
			const match = /^\$\{([^}]+)\}$/.exec(raw);
			if (!match) {
				params[name] = raw;
				continue;
			}
			const value = row[match[1]];
			// Same scalar rule as a cell: a placeholder resolving to an object
			// would put "[object Object]" in a URL, so drop the link instead.
			if (
				typeof value !== "string" &&
				typeof value !== "number" &&
				typeof value !== "boolean"
			) {
				return undefined;
			}
			params[name] = String(value);
		}
		return { ...nav, params };
	}

	/**
	 * A strip card opens its list - or, when it failed, retries.
	 *
	 * GenericTile's own Failed state stays pressable (verified in the framework
	 * source), so the error affordance needs no extra control: the tile IS the
	 * retry button while it is failed.
	 */
	public onTilePress(oEvent: Event): void {
		const ctx = this._contextOf(oEvent);
		if (ctx && ctx.getProperty("state") === "error") {
			this.onRetryPress(oEvent);
			return;
		}
		this._navigateFrom(ctx);
	}

	/**
	 * The pressed control's row in the dashboard model.
	 *
	 * One place for the cast, which four handlers used to repeat. `getSource()`
	 * is typed `EventProvider`, which has no `getBindingContext`, so tsc needs
	 * it; eslint's no-unnecessary-type-assertion disagrees, and the compiler
	 * wins.
	 */
	private _contextOf(oEvent: Event): Context | null | undefined {
		return (oEvent.getSource() as Control).getBindingContext("dashboard");
	}

	/**
	 * A table row opens the record it describes.
	 *
	 * Separate from onTilePress because that one casts its source to
	 * GenericTile. The shared half is _navigateFrom, so the two cannot drift
	 * apart in what they publish.
	 */
	public onTableRowPress(oEvent: Event): void {
		this._navigateFrom(this._contextOf(oEvent));
	}

	// formatValueColor lived here: it mapped a tone onto sap.m.ValueColor for the
	// NumericContent the strip used to render. The strip no longer uses one - the
	// tone reaches the DOM as a data attribute and css/style.css paints the icon
	// square and the footnote from it - so the six tones no longer have to
	// collapse onto ValueColor's four members. If a NumericContent ever comes
	// back, note that it THROWS on an unknown value rather than degrading, so its
	// formatter has to be total.

	/** The card's pinned "Show all (N)" link. */
	public onCardFooterPress(oEvent: Event): void {
		const ctx = this._contextOf(oEvent);
		if (!ctx) return;
		const nav = ctx.getProperty("footer/navigate") as TileNavigate | undefined;
		if (!nav) return;
		this._publishNavigate(nav);
	}

	private _navigateFrom(ctx: Context | null | undefined): void {
		if (!ctx) return;
		const nav = ctx.getProperty("navigate") as TileNavigate | undefined;
		// No navigate block means deliberately inert - not an error.
		if (!nav) return;
		this._publishNavigate(nav);
	}

	/**
	 * The one place the shell's navigation event is published.
	 *
	 * Three callers reach it - a strip card, a table row and a card footer - and
	 * having one publisher is what stops them drifting apart in what they send.
	 */
	private _publishNavigate(nav: TileNavigate): void {
		EventBus.getInstance().publish("HomeChannel", "NavigateToPlugin", {
			pluginId: nav.pluginId,
			view: nav.view,
			params: nav.params,
		});
	}

	/**
	 * Factories, not XML templates, for the table's columns and cells.
	 *
	 * Two reasons, and the second is the load-bearing one:
	 *
	 *  1. The column count is data, so there is no fixed markup to write.
	 *  2. A cloned aggregation binding whose `templateShareable` was never set
	 *     logs "[FUTURE FATAL]" from ManagedObject#cloneBinding, and that call
	 *     becomes a real throw once sap-ui-xx-future is enabled - which would
	 *     take down the whole Dashboard view, KPI tiles included. A factory
	 *     takes the other branch of cloneBinding and cannot hit it.
	 *
	 * The framework-supplied id must be passed through, or each model refresh
	 * builds controls the binding never reclaims.
	 */
	public createTableColumn(sId: string, oContext: Context): Column {
		const column = oContext.getObject() as DashboardTableColumn;
		return new Column(sId, {
			hAlign: column.align,
			demandPopin: column.demandPopin,
			minScreenWidth: column.minScreenWidth,
			header: new Text({ text: column.label }),
		});
	}

	public createTableCell(sId: string, oContext: Context): Control {
		const cell = oContext.getObject() as DashboardTableCell;
		// wrapping: false - a dashboard row is a glance. A long customer name
		// ellipsises rather than pushing the table to two lines per row.
		return new Text(sId, { text: cell.text, wrapping: false });
	}
}
